import React, { useEffect, useRef, useState } from "react";
import {
  ActionIcon,
  Avatar,
  Badge,
  Box,
  Button,
  createStyles,
  Flex,
  ScrollArea,
  Text,
  Textarea,
} from "@mantine/core";
import {
  IconArrowRight,
  IconMicrophone,
  IconSend,
  IconShieldCheck,
} from "@tabler/icons-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import AppNavbar from "../../../components/AppNavbar/AppNavbar";
import { getStateRequirements, sendSkAiMessage } from "../../../services/compliance";

// ─── Styles ────────────────────────────────────────────────────────────────────
const useStyles = createStyles(() => ({
  page: {
    height: "100vh",
    width: "100%",
    display: "flex",
    flexDirection: "column",
    backgroundColor: "#F5F5F5",
    overflow: "hidden",
  },
  scrollArea: {
    flex: 1,
    padding: "16px 20px",
  },
  // SkAi message bubble
  skAiBubble: {
    backgroundColor: "#FFF",
    border: "1px solid #E5E7EB",
    borderRadius: "4px 14px 14px 14px",
    padding: "12px 14px",
    maxWidth: "72%",
    lineHeight: 1.6,
    fontSize: 14,
    color: "#111827",
  },
  // User message bubble
  userBubble: {
    backgroundColor: "#1E293B",
    borderRadius: "14px 14px 4px 14px",
    padding: "12px 14px",
    maxWidth: "72%",
    lineHeight: 1.6,
    fontSize: 14,
    color: "#FFF",
  },
  // Requirement card (appears below SkAi bubble)
  reqCard: {
    border: "1px solid #E5E7EB",
    borderRadius: 10,
    padding: "10px 14px",
    backgroundColor: "#FAFAFA",
    marginTop: 8,
    maxWidth: "72%",
    marginLeft: 44, // align with bubble (past avatar)
  },
  reqCategory: {
    backgroundColor: "var(--app-tint-primary)",
    color: "var(--app-primary)",
    border: "1px solid var(--app-primary)",
    fontWeight: 700,
    fontSize: 10,
  },
  logBtn: {
    backgroundColor: "transparent",
    color: "var(--app-primary)",
    border: "1px solid var(--app-primary)",
    borderRadius: 6,
    fontWeight: 600,
    fontSize: 12,
    padding: "3px 10px",
    height: "auto",
    flexShrink: 0,
    "&:hover": { backgroundColor: "var(--app-tint-primary)" },
  },
  // Typing dots animation
  dot: {
    width: 7,
    height: 7,
    borderRadius: "50%",
    backgroundColor: "#9CA3AF",
    animation: "skaiDot 1.2s ease-in-out infinite",
  },
  // Input area footer
  inputArea: {
    backgroundColor: "#FFF",
    borderTop: "1px solid #E5E7EB",
    padding: "12px 20px",
  },
  textarea: {
    flex: 1,
    "& textarea": {
      borderRadius: 10,
      border: "1px solid #E5E7EB",
      fontSize: 14,
      resize: "none",
      minHeight: 42,
      maxHeight: 120,
      "&:focus": { borderColor: "var(--app-primary)" },
    },
  },
  sendBtn: {
    backgroundColor: "var(--app-primary)",
    color: "var(--app-on-primary)",
    borderRadius: 10,
    width: 42,
    height: 42,
    flexShrink: 0,
    "&:hover": { backgroundColor: "#f5a53c" },
    "&:disabled": { backgroundColor: "#F3F4F6", color: "#9CA3AF" },
  },
}));

// ─── ComplianceOfficer ─────────────────────────────────────────────────────────
const ComplianceOfficer = () => {
  const { classes } = useStyles();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { profile } = useSelector((state) => state?.profile);

  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [initialising, setInitialising] = useState(true);

  const scrollRef = useRef(null);
  const textareaRef = useRef(null);

  // ── Scroll to bottom whenever messages change ──
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
    }
  }, [messages, loading]);

  // ── On mount: load requirements → send greeting → handle ?q= param ──
  useEffect(() => {
    const init = async () => {
      setInitialising(true);

      // 1. Load state requirements (pre-warms cache on backend)
      await getStateRequirements();

      // 2. Send silent greeting trigger to SkAi
      const greetingMsg =
        "Hi SkAi Beam, please introduce yourself briefly and give me a " +
        "proactive briefing on my most urgent compliance items right now. " +
        "Be warm, direct, and keep it under 120 words.";

      const greetResult = await sendSkAiMessage(greetingMsg, []);
      if (greetResult?.reply) {
        setMessages([
          {
            role: "assistant",
            content: greetResult.reply,
            requirements: greetResult.surfacedRequirements || [],
          },
        ]);
      }

      setInitialising(false);

      // 3. If navigated from widget quick-input, send that message right away
      const prefilledQ = searchParams.get("q");
      if (prefilledQ) {
        setInput(prefilledQ);
        // give React a tick to update input, then send
        setTimeout(() => sendMessage(prefilledQ, [
          {
            role: "assistant",
            content: greetResult?.reply || "",
          },
        ]), 50);
      }
    };

    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Send a message ──
  const sendMessage = async (text, existingHistory) => {
    const msgText = text || input.trim();
    if (!msgText || loading) return;

    const userMsg = { role: "user", content: msgText };
    setMessages((prev) => [...prev, userMsg]);
    if (!text) setInput("");
    setLoading(true);

    const history = (existingHistory || messages).map((m) => ({
      role: m.role,
      content: m.content,
    }));

    const result = await sendSkAiMessage(msgText, history);

    if (result?.reply) {
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: result.reply,
          requirements: result.surfacedRequirements || [],
        },
      ]);
    }

    setLoading(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // ─── Render ──────────────────────────────────────────────────────────────────
  return (
    <Box className={classes.page}>
      {/* Navbar */}
      <AppNavbar title="Compliance Officer" profile={profile} />

      {/* Message Area */}
      <ScrollArea
        className={classes.scrollArea}
        viewportRef={scrollRef}
        style={{ flex: 1, overflow: "hidden auto" }}
      >
        {/* SkAi Status Row */}
        <Flex align="center" gap={8} mb={20}>
          <Avatar
            size={32}
            radius="md"
            style={{
              background: "linear-gradient(135deg, #1E293B 0%, #334155 100%)",
              color: "var(--app-primary)",
            }}
          >
            <IconShieldCheck size={16} />
          </Avatar>
          <Box>
            <Text size="xs" weight={700} color="#111827">
              SkAi Beam
            </Text>
            <Flex align="center" gap={4}>
              <Box
                style={{
                  width: 7,
                  height: 7,
                  borderRadius: "50%",
                  backgroundColor: initialising ? "#F59E0B" : "#10B981",
                }}
              />
              <Text size="xs" color="dimmed">
                {initialising ? "Getting your briefing ready…" : "Online — ready to help"}
              </Text>
            </Flex>
          </Box>
        </Flex>

        {/* Messages */}
        {messages.map((msg, idx) =>
          msg.role === "assistant" ? (
            <Box key={idx} mb={20}>
              {/* SkAi bubble */}
              <Flex align="flex-start" gap={10}>
                <Avatar
                  size={32}
                  radius="md"
                  style={{
                    background: "linear-gradient(135deg, #1E293B 0%, #334155 100%)",
                    color: "var(--app-primary)",
                    flexShrink: 0,
                  }}
                >
                  <IconShieldCheck size={16} />
                </Avatar>
                <Box className={classes.skAiBubble}>
                  <Text style={{ whiteSpace: "pre-wrap" }}>{msg.content}</Text>
                </Box>
              </Flex>

              {/* Requirement cards under bubble */}
              {msg.requirements?.length > 0 &&
                msg.requirements.map((req, ri) => (
                  <RequirementCard
                    key={ri}
                    req={req}
                    classes={classes}
                    onLog={() => navigate("/home/compliance")}
                  />
                ))}
            </Box>
          ) : (
            // User bubble
            <Flex key={idx} justify="flex-end" mb={20}>
              <Box className={classes.userBubble}>
                <Text style={{ whiteSpace: "pre-wrap" }}>{msg.content}</Text>
              </Box>
            </Flex>
          )
        )}

        {/* Typing indicator */}
        {(initialising || loading) && (
          <Flex align="flex-start" gap={10} mb={20}>
            <Avatar
              size={32}
              radius="md"
              style={{
                background: "linear-gradient(135deg, #1E293B 0%, #334155 100%)",
                color: "var(--app-primary)",
                flexShrink: 0,
              }}
            >
              <IconShieldCheck size={16} />
            </Avatar>
            <Box
              style={{
                backgroundColor: "#FFF",
                border: "1px solid #E5E7EB",
                borderRadius: "4px 14px 14px 14px",
                padding: "14px 18px",
                display: "flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              <TypingDots />
            </Box>
          </Flex>
        )}
      </ScrollArea>

      {/* Input Bar */}
      <Box className={classes.inputArea}>
        <Flex gap={10} align="flex-end">
          {/* Mic — phase 2 placeholder */}
          <ActionIcon
            size={42}
            radius={10}
            variant="default"
            disabled
            title="Voice input — coming soon"
            style={{ flexShrink: 0 }}
          >
            <IconMicrophone size={18} color="#9CA3AF" />
          </ActionIcon>

          <Textarea
            ref={textareaRef}
            className={classes.textarea}
            placeholder="Ask me anything about compliance…"
            value={input}
            onChange={(e) => setInput(e.currentTarget.value)}
            onKeyDown={handleKeyDown}
            minRows={1}
            maxRows={4}
            autosize
            disabled={initialising}
          />

          <ActionIcon
            className={classes.sendBtn}
            onClick={() => sendMessage()}
            disabled={!input.trim() || loading || initialising}
            title="Send"
          >
            <IconSend size={18} />
          </ActionIcon>
        </Flex>

        <Text size={11} color="dimmed" mt={6} align="center">
          SkAi Beam references only your state's approved regulations.
        </Text>
      </Box>

      {/* Keyframe animation for typing dots (injected once) */}
      <style>{`
        @keyframes skaiDot {
          0%, 80%, 100% { transform: scale(0.6); opacity: 0.4; }
          40% { transform: scale(1); opacity: 1; }
        }
      `}</style>
    </Box>
  );
};

// ─── RequirementCard ───────────────────────────────────────────────────────────
const RequirementCard = ({ req, classes, onLog }) => (
  <Box className={classes.reqCard}>
    <Flex justify="space-between" align="center" gap={10}>
      <Box style={{ flex: 1, minWidth: 0 }}>
        <Badge className={classes.reqCategory} size="xs" mb={4}>
          {req.category}
        </Badge>
        <Text size="sm" weight={600} color="#111827" style={{ lineHeight: 1.3 }}>
          {req.name}
        </Text>
        <Text size="xs" color="dimmed" mt={2}>
          Every {req.frequency?.value} {req.frequency?.unit}
          {req.regulatoryReference ? ` · ${req.regulatoryReference}` : ""}
        </Text>
        {req.approvedAt && (
          <Text size="xs" color="#10B981" mt={3} weight={500}>
            Last approved by Ogiso: {new Date(req.approvedAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
          </Text>
        )}
      </Box>
      <Button
        className={classes.logBtn}
        rightIcon={<IconArrowRight size={12} />}
        onClick={onLog}
        compact
      >
        Log it now
      </Button>
    </Flex>
  </Box>
);

// ─── TypingDots ────────────────────────────────────────────────────────────────
const TypingDots = () => (
  <>
    {[0, 1, 2].map((i) => (
      <Box
        key={i}
        style={{
          width: 7,
          height: 7,
          borderRadius: "50%",
          backgroundColor: "#9CA3AF",
          animation: `skaiDot 1.2s ease-in-out ${i * 0.2}s infinite`,
        }}
      />
    ))}
  </>
);

export default ComplianceOfficer;
